import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import SEO from "@/components/SEO";

export function BlogList() {
  const { t } = useI18n();
  const [blogs, setBlogs] = useState([]);
  useEffect(() => { api.get("/blogs").then(({ data }) => setBlogs(data)); }, []);
  return (
    <div data-testid="blog-list">
      <SEO title="Blog" description="House design, construction and cost-saving tips from the NakshaCart team." path="/blog" />
      <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
        <div className="nc-container py-12 md:py-14">
          <h1 className="text-3xl md:text-4xl font-semibold text-slate-900">{t("blog.title")}</h1>
          <p className="text-slate-600 mt-2">{t("blog.sub")}</p>
        </div>
      </div>
      <div className="nc-container py-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {blogs.map(b => (
          <Link to={`/blog/${b.slug}`} key={b.id} className="nc-card overflow-hidden group" data-testid={`blog-card-${b.slug}`}>
            <div className="aspect-video bg-slate-100 overflow-hidden">
              <img src={b.cover_image} alt={b.cover_image_alt || b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <h3 className="font-semibold text-slate-900 line-clamp-2">{b.title}</h3>
              {b.subtitle && <p className="text-sm text-slate-700 mt-1 line-clamp-2">{b.subtitle}</p>}
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{b.excerpt}</p>
              <p className="text-xs text-slate-400 mt-3">{new Date(b.published_at).toLocaleDateString()}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function BlogContent({ content }) {
  if (!content) return null;
  // Heuristic: treat as HTML if it contains common block tags from the rich editor.
  // Legacy plain-text posts (no tags) fall through to the paragraph-split renderer.
  const looksHtml = /<(p|h[1-6]|ul|ol|li|blockquote|pre|img|a|strong|em|br|div)[\s>/]/i.test(content);
  if (looksHtml) {
    return (
      <div
        className="mt-8 nc-blog-content"
        dangerouslySetInnerHTML={{ __html: content }}
        data-testid="blog-content-html"
      />
    );
  }
  return (
    <div className="mt-8 prose prose-slate max-w-none" data-testid="blog-content-text">
      {content.split("\n\n").map((p, i) => (
        <p key={i} className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line">{p}</p>
      ))}
    </div>
  );
}

function youtubeEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export function BlogPost() {
  const { t } = useI18n();
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  useEffect(() => { api.get(`/blogs/${slug}`).then(({ data }) => setBlog(data)); }, [slug]);
  if (!blog) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  const embedUrl = youtubeEmbedUrl(blog.youtube_url);
  return (
    <div className="bg-white" data-testid="blog-post">
      <SEO
        title={blog.title}
        description={blog.excerpt || blog.subtitle}
        path={`/blog/${blog.slug}`}
        image={blog.cover_image}
      />
      <div className="nc-container py-8 max-w-3xl">
        <Link to="/blog" className="text-sm text-slate-500 inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> {t("blog.allArticles")}</Link>
        <h1 className="mt-4 text-3xl md:text-4xl font-semibold text-slate-900">{blog.title}</h1>
        {blog.subtitle && <p className="mt-2 text-lg text-slate-600">{blog.subtitle}</p>}
        <p className="text-sm text-slate-500 mt-2">{blog.author} • {new Date(blog.published_at).toLocaleDateString()}</p>
        {blog.cover_image && !embedUrl && <img src={blog.cover_image} alt={blog.cover_image_alt || blog.title} className="mt-6 rounded-2xl w-full object-cover aspect-[16/9]" />}
        {embedUrl && (
          <div className="mt-6 rounded-2xl overflow-hidden aspect-video w-full">
            <iframe
              src={embedUrl}
              title={blog.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"
            />
          </div>
        )}
        <BlogContent content={blog.content} />
      </div>
    </div>
  );
}
